import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import VideoPlayerModal from './components/VideoPlayerModal';
import { getVideosForTrainee } from '../../api_service';

const MyVideos = () => {
  const navigate = useNavigate();
  const traineeId = sessionStorage.getItem('userId');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playTarget, setPlayTarget] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadVideos(); }, []);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const res = await getVideosForTrainee(traineeId);
      setVideos(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const isCompleted = (item) => !!item.progress?.completed;
  const isInProgress = (item) => !item.progress?.completed && (item.progress?.watchedSeconds || 0) > 0;

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error bg-error/10' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning bg-warning/10' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning bg-warning/10' };
    return { text: `${diff} days left`, cls: 'text-muted-foreground bg-muted/40' };
  };

  const watchedPct = (item) => {
    if (item.progress?.completed) return 100;
    const dur = item.video?.durationSeconds;
    if (!dur || dur <= 0) return 0;
    return Math.min(100, Math.round(((item.progress?.watchedSeconds || 0) / dur) * 100));
  };

  const filtered = videos.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'completed') return isCompleted(item);
    if (filter === 'inProgress') return !isCompleted(item);
    return true;
  });

  const completedCount = videos.filter(isCompleted).length;
  const inProgressCount = videos.length - completedCount;

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="trainee"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Videos</h1>
          <p className="text-sm text-muted-foreground mt-1">Watch your assigned training videos</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { key: 'all',        label: 'Total',       icon: 'Video',      color: 'text-primary', count: videos.length },
            { key: 'inProgress', label: 'In Progress', icon: 'Clock',      color: 'text-warning', count: inProgressCount },
            { key: 'completed',  label: 'Completed',   icon: 'CheckCircle', color: 'text-success', count: completedCount },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`bg-card border rounded-xl p-4 text-left transition-all ${filter === s.key ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/40'}`}
            >
              <Icon name={s.icon} size={20} className={`${s.color} mb-2`} />
              <p className="text-2xl font-bold text-foreground">{s.count}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Video grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading videos...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="Video" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No videos found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map(item => {
              const pct = watchedPct(item);
              const completed = isCompleted(item);
              const dl = deadlineLabel(item.video?.deadline);
              return (
                <div key={item.video?.videoId} className="bg-card border border-border rounded-xl overflow-hidden flex flex-col">
                  {/* Thumbnail placeholder */}
                  <div className="relative aspect-video bg-muted flex items-center justify-center">
                    <Icon name="PlayCircle" size={44} className="text-muted-foreground" />
                    {completed && (
                      <span className="absolute top-2 right-2 text-xs font-medium px-2.5 py-0.5 rounded-full bg-success/10 text-success">
                        Completed
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-foreground line-clamp-2">{item.video?.title}</h3>
                      {dl && (
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${dl.cls}`}>{dl.text}</span>
                      )}
                    </div>
                    {item.video?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{item.video.description}</p>
                    )}

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${completed ? 'bg-success' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-9 text-right">{pct}%</span>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Deadline: {item.video?.deadline || 'None'}</span>
                      <Button size="sm" onClick={() => setPlayTarget(item)} iconName="Play" iconSize={14}>
                        Watch
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {playTarget && (
        <VideoPlayerModal
          item={playTarget}
          traineeId={traineeId}
          onClose={() => setPlayTarget(null)}
          onUpdated={loadVideos}
        />
      )}
    </div>
  );
};

export default MyVideos;
